import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, TextField, DialogActions, Stack, IconButton, Alert, CircularProgress } from '@mui/material';
import { Button } from 'components/index';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Division, LineItemUnitEnum } from '../glossary/glossary.type';
import { ReportGeneralInfo, ReportLineItemBulkEditFormValues, ReportLineItemNotations } from './report.type';
import Autocomplete from '@mui/material/Autocomplete';
import { ProjectDraw } from '../project/project.type';
import { Close } from '@mui/icons-material';
import SelectDivisionFromGiven from '../glossary/SelectDivisionFromGiven';
import SelectInput from '../SelectInput';
import { getQuantity } from './report.util';
import { useLineItemSearch } from '../../hooks/useLineItemSearch';

// ... other imports
interface CreateLineItemDialogProps {
  open: boolean;
  onClose: () => void;
  currentDivision: Division | null;
  divisions: Division[];
  onSubmit?: (values: ReportLineItemBulkEditFormValues) => void;
  saveLineItem: (values: any) => Promise<void>;
  reportGeneralInfo: ReportGeneralInfo;
  projectDraws?: ProjectDraw[];
  useOriginalBudget?: boolean;
}
const unitsList = [...Object.values(LineItemUnitEnum).map(unit => ({ value: unit, label: unit }))];
const notationsList = [...Object.values(ReportLineItemNotations).map(notation => ({ value: notation, label: notation }))];

const CreateReportLineItemDialog: React.FC<CreateLineItemDialogProps> = ({
  open,
  onClose,
  currentDivision,
  divisions,
  saveLineItem,
  reportGeneralInfo,
  projectDraws,
  useOriginalBudget = false,
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const sortedDivisions = divisions.sort((a, b) => parseInt(a.code) - parseInt(b.code));
  const isProjectLineItem = Boolean(projectDraws);

  // Use the custom hook for line item search
  const { lineItemOptions, isSearching, searchLineItems } = useLineItemSearch(open);

  const initialFormValues = {
    description: '',
    unit: unitsList[0].value,
    quantity: getQuantity(unitsList[0].value, reportGeneralInfo),
    totalBudget: 0,
    originalBudget: 0,
    divisionId: currentDivision?.id || null,
    notation: '',
    projectDrawId: projectDraws?.[0]?.id || null,
  };

  const formik = useFormik({
    initialValues: { ...initialFormValues },
    enableReinitialize: true,
    validationSchema: yup.object({
      description: yup.string().required('Description is required'),
      unit: yup.string().required('Unit is required'),
      quantity: yup.number().positive('Quantity must be positive').required('Quantity is required'),
      // Conditionally validate either totalBudget or originalBudget
      ...(useOriginalBudget
        ? { originalBudget: yup.number().required('Original Budget is required') }
        : { totalBudget: yup.number().required('Total Budget is required') }),
    }),
    onSubmit: async values => {
      setIsCreating(true);
      await saveLineItem(values);
      setIsCreating(false);
      onClose();
    },
  });

  if (projectDraws && projectDraws.length == 0) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          Add New Line Item
          <IconButton onClick={onClose} sx={{ marginLeft: 'auto' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} py={2}>
            <Alert severity='error'>No Project Budgets available!</Alert>
            <Alert severity='info'>Head over to project page and create a budget first...</Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' color='error' onClick={onClose}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        Add New Line Item
        <IconButton onClick={onClose} sx={{ marginLeft: 'auto' }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={2} py={2}>
            {/* Autocomplete for Description */}
            <Autocomplete
              freeSolo
              filterOptions={() => lineItemOptions}
              options={lineItemOptions}
              getOptionLabel={option => {
                if (typeof option === 'string') return option;
                return option.description || '';
              }}
              loading={isSearching}
              value={formik.values.description}
              onChange={(event, newValue) => {
                const description = typeof newValue === 'string' ? newValue : newValue?.description || '';
                formik.setFieldValue('description', description);
              }}
              onInputChange={(event, newInputValue) => {
                formik.setFieldValue('description', newInputValue);
                searchLineItems(newInputValue);
              }}
              id='description-autocomplete'
              renderInput={params => (
                <TextField
                  {...params}
                  label='Description'
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                  fullWidth
                  variant='standard'
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isSearching ? <CircularProgress color='inherit' size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                const displayText = typeof option === 'string' ? option : option.description || '';
                const key = typeof option === 'string' ? option : option.id || option.description || '';
                return (
                  <li {...props} key={key}>
                    {displayText}
                  </li>
                );
              }}
            />

            {/* SelectInput for Division */}
            <SelectDivisionFromGiven
              label='Division'
              divisions={sortedDivisions}
              selectedDivision={formik.values.divisionId ?? null}
              setSelectedDivision={newDivisionId => {
                formik.setFieldValue('divisionId', newDivisionId);
              }}
            />

            {/* SelectInput for Unit */}
            <SelectInput
              label='Unit'
              selected={formik.values.unit ?? ''}
              setSelected={newVal => {
                formik.setFieldValue('quantity', getQuantity(newVal, reportGeneralInfo));
                formik.setFieldValue('unit', newVal === '' ? null : newVal);
              }}
              list={unitsList}
            />

            {/* TextFields for Quantity and Original Budget */}
            {/* ... */}
            <TextField
              fullWidth
              margin='normal'
              id='quantity'
              name='quantity'
              label='Quantity'
              type='number'
              value={formik.values.quantity}
              variant='standard'
              onChange={formik.handleChange}
              error={formik.touched.quantity && Boolean(formik.errors.quantity)}
              helperText={formik.touched.quantity && formik.errors.quantity}
            />

            <TextField
              fullWidth
              margin='normal'
              id={useOriginalBudget ? 'originalBudget' : 'totalBudget'}
              name={useOriginalBudget ? 'originalBudget' : 'totalBudget'}
              label={useOriginalBudget ? 'Original Budget' : 'Total Budget'}
              type='number'
              value={useOriginalBudget ? formik.values.originalBudget : formik.values.totalBudget}
              variant='standard'
              onChange={formik.handleChange}
              error={
                formik.touched[useOriginalBudget ? 'originalBudget' : 'totalBudget'] &&
                Boolean(formik.errors[useOriginalBudget ? 'originalBudget' : 'totalBudget'])
              }
              helperText={
                formik.touched[useOriginalBudget ? 'originalBudget' : 'totalBudget'] &&
                formik.errors[useOriginalBudget ? 'originalBudget' : 'totalBudget']
              }
            />

            {isProjectLineItem ? (
              <SelectInput
                label='Project Draw'
                selected={formik.values.projectDrawId ?? ''}
                setSelected={newVal => {
                  formik.setFieldValue('projectDrawId', newVal);
                }}
                list={projectDraws?.map(draw => ({ value: draw.id, label: draw.name })) || []}
              />
            ) : (
              <Autocomplete
                disablePortal
                freeSolo
                id='notation'
                options={notationsList}
                inputValue={formik.values.notation ?? ''}
                onInputChange={(_, newVal) => {
                  formik.setFieldValue('notation', newVal === '' ? null : newVal);
                }}
                renderInput={params => <TextField {...params} name='notation' label='Notation' variant='standard' />}
              />
            )}
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='error' onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant='contained'
          loading={isCreating}
          onClick={() => {
            formik.submitForm();
          }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateReportLineItemDialog;
